import IconAWS from '@apollo/icons/default/IconAWS.svg';
import IconAzure from '@apollo/icons/default/IconAzure.svg';
import IconGCP from '@apollo/icons/default/IconGCP.svg';

import IconDocker from 'src/assets/icon-docker-logo.svg';

export interface DeploymentProvider {
  /** Unique identifier for the deployment provider */
  id: string;
  /** Display name of the provider */
  name: string;
  /** Brief description of the deployment option */
  description: string;
  /** Type of action when clicked */
  type: 'link' | 'modal' | 'guided';
  /** Text for the action button */
  action: string;
  /** URL for link-type providers */
  url?: string;
  /** onClick handler for guided-type providers */
  onClick?: () => void;
  /** Optional icon component for the provider */
  icon?: React.ComponentType<{ className?: string }>;
  /** Template URL for guided deployment providers */
  templateUrl?: string;
  /** Default endpoint URL for deployment instructions */
  defaultEndpoint?: string;
}

/**
 * Centralized platform configuration for deployment flow
 * Single source of truth for all platform information
 */

export const DEPLOYMENT_PLATFORMS: Record<string, DeploymentProvider> = {
  railway: {
    id: 'railway',
    name: 'Railway',
    description:
      'Use a Railway template to quickly spin up Apollo Router. No config needed; your graph gets a live endpoint right away.',
    type: 'guided',
    action: 'Continue with Railway',
    templateUrl: 'https://railway.com/new/template/apollo-router',
    defaultEndpoint: 'https://graph-production.up.railway.app',
  },
  render: {
    id: 'render',
    name: 'Render',
    description:
      'Provision Apollo Router on Render with pre-configured settings.',
    type: 'guided',
    action: 'Continue with Render',
    templateUrl:
      'https://render.com/deploy?repo=https://github.com/apollographql/router-template/tree/runtime',
    defaultEndpoint: 'https://graphos-router.onrender.com',
  },
  gcp: {
    id: 'gcp',
    name: 'Google Cloud Run',
    description: '',
    type: 'link',
    action: 'Follow guide',
    url: 'https://www.apollographql.com/docs/graphos/routing/self-hosted/containerization/gcp',
    icon: IconGCP,
  },
  aws: {
    id: 'aws',
    name: 'ECS with Fargate',
    description: '',
    type: 'link',
    action: 'Follow guide',
    url: 'https://www.apollographql.com/docs/graphos/routing/self-hosted/containerization/aws',
    icon: IconAWS,
  },
  azure: {
    id: 'azure',
    name: 'Azure',
    description: '',
    type: 'link',
    action: 'Follow guide',
    url: 'https://www.apollographql.com/docs/graphos/routing/self-hosted/containerization/azure',
    icon: IconAzure,
  },
  docker: {
    id: 'docker',
    name: 'Docker',
    description: '',
    type: 'link',
    action: 'Follow guide',
    url: 'https://www.apollographql.com/docs/graphos/routing/self-hosted/containerization/docker',
    icon: IconDocker,
  },
};

/**
 * Get quick deployment options (guided type platforms)
 */
export const getQuickDeploymentOptions = (): DeploymentProvider[] => {
  return Object.values(DEPLOYMENT_PLATFORMS).filter(
    (platform) => platform.type === 'guided',
  );
};

/**
 * Get cloud provider options (link type platforms)
 */
export const getCloudProviderOptions = (): DeploymentProvider[] => {
  return Object.values(DEPLOYMENT_PLATFORMS).filter(
    (platform) => platform.type === 'link',
  );
};

/**
 * Get platform configuration by ID
 */
export const getPlatformById = (id: string): DeploymentProvider | undefined => {
  return Object.prototype.hasOwnProperty.call(DEPLOYMENT_PLATFORMS, id)
    ? DEPLOYMENT_PLATFORMS[id]
    : undefined;
};
